import { v4 as uuidv4 } from 'uuid';

import type { ImportedGroupMeta } from '../../utils/csv-grouping/csv-group-meta';
import type { TableNode } from '..';
import { isGroupRow } from './group-row-guards';

/**
 * Project Bobcat — propose real groups for onboarding-declared "task" stub
 * nodes: the "Create Solution (conversational, beta)" flow's use-case list,
 * and the equivalent Supervisor-settings Tasks form (`use-supervisor-
 * form.tsx`) — both create a node per declared task via the same
 * `task-utils.ts` (`createTaskNode`), landing ungrouped.
 *
 * A Use Case Agent is a real group, same as Supervisor Agent or Knowledge
 * Agent — a third named category, not a display-only bucket for ungrouped
 * rows (see `reference_use_case_agents_category` memory). This module does
 * not reopen that: it proposes
 * turning each stub into its OWN single-member group, named from the stub's
 * own `nodeName`, so it stops being ungrouped and becomes a real group of
 * that third category. It is additive convenience on top of the corrected
 * model, not a new kind of bucket.
 *
 * ## Detection is by `settings.taskId`, NOT by node number — a real
 * regression forced this correction
 *
 * The first version of this module detected by node NUMBER: a solution
 * created live through the conversational flow had its three declared tasks
 * land at 99991/99992/99993, so detection was "ungrouped and numbered
 * 99991 or above, no confirmed ceiling." That shipped, passed every test
 * against the two small reference solutions ("Bobcat!", "Harmony Music
 * Store") — and then failed live verification against "RBI Voice" (520
 * real nodes, a genuine Burger King voice IVR import): 314 of its 520 nodes
 * — real production nodes like `SlackLogger` (999741) and `Error action
 * node` (99991, the EXACT floor value) — false-matched, because Pypestream
 * solutions independently use the 999xxx+ number space for ordinary
 * system/error/utility nodes that have nothing to do with onboarding. A
 * floor-only check cannot tell those apart from a real task stub; RBI
 * Voice proves the collision is real, not hypothetical, at real scale.
 *
 * The fix: `task-utils.ts` already has a purpose-built, unambiguous marker.
 * `createTaskNode` stamps every task node it creates with
 * `settings.taskId` (a real id cross-checked against
 * `draftVersionSolutionData.tasks` elsewhere in that file, via `isTaskRow`).
 * Verified live: the real onboarding-generated stub at node 99991 in
 * "Harmony Music Store" carries `settings.taskId`; RBI Voice's false-positive
 * nodes (checked directly, by raw row) carry no such field. This is the
 * actual, intentional identity of a task node — not an inferred proxy for
 * one — so it is the only signal this module uses now.
 *
 * ## Why single-member groups, not a run
 *
 * `auto-agent-grouping.ts`'s Supervisor/Knowledge groups are contiguous RUNS
 * because those pipelines are multi-step and interleaved with filler. A
 * task stub is a single placeholder node — there is nothing to run across —
 * so each qualifying row becomes its own group of one. Nothing here stops a
 * later, separately-built step from letting a user merge stub groups or add
 * more members by hand; that is ordinary group editing, not something this
 * planner does.
 *
 * ## Same "detected, not automatic" pattern as auto-agent-grouping — but NOT
 * the same write path
 *
 * Pure and dependency-light, returns an `ImportedGroupMeta[]` in the same
 * shape `planAutoAgentGroups` does: this module only proposes,
 * `table-store.ts`'s `applyUseCaseAgentGrouping` is the only thing that
 * writes, and only when a person asks for it.
 *
 * It does NOT feed that plan into `applyImportedGrouping`, though — found
 * live, not assumed: `planImportedGroups` (`csv-group-meta.ts`) requires at
 * least 2 matched members per group and silently skips anything smaller, so
 * every single-member group this planner proposes would be dropped without
 * error. `applyUseCaseAgentGrouping` writes directly instead. See its doc
 * comment for why a floor built for CSV-import noise and manual multi-select
 * does not apply to a system-detected, one-stub-to-one-group conversion.
 */

/**
 * The single fixed colour every proposed use-case group gets, the same way
 * Supervisor and Knowledge each get one fixed colour for their whole
 * category. `#FFA800` — a real preset from the group-color picker's palette
 * (`GROUP_COLOR_PRESETS`, `draggable-row.tsx`), chosen because it is the only
 * preset neither Supervisor (`#8573E7`) nor Knowledge (`#34785A`) already
 * uses.
 */
export const USE_CASE_AGENT_GROUP_COLOR = '#FFA800';

/**
 * Project Bobcat — one colour per use-case agent, as the prototype draws
 * them (`AGENT_STEPS`, "Group colours: soft"): eight hues at two steps,
 * alternating cool and warm so neighbouring agents stay far apart. None is
 * the Supervisor's or the Knowledge Agent's, and none is red: red is how the
 * table marks an error, and an agent drawn in it reads as failing. Canvas,
 * Table and Design soften them the same way (`agent-palette.ts`).
 */
export const USE_CASE_AGENT_COLORS = [
  '#016FEF',
  '#00838F',
  '#0B7BB9',
  '#A46804',
  '#BD3EB6',
  '#10873F',
  '#8358EB',
  '#BC5806',
  '#0057BF',
  '#00666F',
  '#056193',
  '#825205',
  '#9F1B9A',
  '#026A2E',
  '#6A3BCC',
  '#964606',
] as const;

/** The first palette colour no agent uses yet; past sixteen, they repeat. */
export const nextUseCaseAgentColor = (
  inUse: readonly (string | null | undefined)[]
): string => {
  const used = new Set(
    inUse.filter((c): c is string => !!c).map((c) => c.toUpperCase())
  );
  return (
    USE_CASE_AGENT_COLORS.find((c) => !used.has(c)) ??
    USE_CASE_AGENT_COLORS[used.size % USE_CASE_AGENT_COLORS.length]
  );
};

/** A non-empty `settings.taskId` — `task-utils.ts`'s own marker for a task node. */
const hasTaskId = (row: TableNode): boolean =>
  typeof row.settings?.taskId === 'string' && row.settings.taskId.length > 0;

/**
 * Rows this planner may propose a group for: not a group header, not already
 * in a group, and carrying `task-utils.ts`'s own `settings.taskId` marker.
 *
 * Shared by detection and planning so the two can never disagree about which
 * rows they are looking at — same discipline as `auto-agent-grouping.ts`'s
 * `eligibilityOf`.
 */
export const isUseCaseAgentStubRow = (row: TableNode): boolean => {
  if (isGroupRow(row) || row.settings?.groupId) return false;
  return hasTaskId(row);
};

/** The ungrouped task stub rows in `rows`, in their existing order. */
export const findUseCaseAgentStubRows = (rows: TableNode[]): TableNode[] =>
  rows.filter(isUseCaseAgentStubRow);

/**
 * The stub's own name, trimmed — that is what the user typed as their task
 * name, so it is the truest available label for the group. Falls back to a
 * numbered placeholder only when the name is missing or blank, which should
 * not happen for a real task node (`createTaskNode` always sets it from the
 * declared task name) but keeps the planner from emitting an unnamed group
 * if it does.
 */
const labelFor = (row: TableNode, ordinal: number): string => {
  const name = typeof row.nodeName === 'string' ? row.nodeName.trim() : '';
  return name.length ? name : `Use Case Agent ${ordinal}`;
};

/**
 * Propose one single-member group per qualifying stub row in `rows`.
 *
 * Returns `[]` when there is nothing to propose — no stub rows, or every
 * stub row is already grouped. Unlike `planAutoAgentGroups`, this never
 * looks at whether the SOLUTION carries other grouping elsewhere: a stub
 * group is independent of any Supervisor/Knowledge groups already present,
 * so this must not be gated on `hasAnyGrouping`.
 */
export const planUseCaseAgentGroups = (
  rows: TableNode[]
): ImportedGroupMeta[] => {
  const stubs = findUseCaseAgentStubRows(rows);
  // Each agent its own colour, after any the solution's groups already use.
  const inUse: (string | null | undefined)[] = rows
    .filter(isGroupRow)
    .map((r) => r.settings?.groupColor);

  return stubs.map((row, index) => {
    const groupColor = nextUseCaseAgentColor(inUse);
    inUse.push(groupColor);
    return {
      groupId: uuidv4(),
      groupName: labelFor(row, index + 1),
      groupColor,
      // Collapsed by default, matching `planAutoAgentGroups` — consistent
      // resting state for every group this session's Bobcat features propose.
      collapsed: true,
      members: [{ nodeNumber: Number(row.nodeNumber), order: 1 }],
    };
  });
};
